import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { flatMap as _flatMap, get as _get } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { addVehicleBindQuery, addVehicleInfoQuery } from '@amfam/policy/add-vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { OverviewAnalytics } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';

@Component({
  selector: 'ds-vehicle-driver-info',
  templateUrl: './vehicle-driver-info.component.html',
  styleUrls: ['./vehicle-driver-info.component.scss'],
  standalone: false
})
export class VehicleDriverInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<unknown>();
  @Output() notifyAgentEvent = new EventEmitter<unknown>();

  public driversList$ = this.store.select(addVehicleInfoQuery.selectNewVehicleDrivers);
  public driverNamesList$ = this.driversList$.pipe(
    map(driverList => _flatMap(driverList, driver => driver.name))
  );
  public driversInfoForm: UntypedFormGroup = this.formBuilder.group({});
  public authorized$ = this.roleService.isAuthorized('addvehicle_sendtoagent');
  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: LoadingSpinnerService,
    public roleService: ImpersonateRolesService,
    private copyService: CopyService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.handleError();
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'HouseholdDrivers' }
      })
    );
  }

  correctInfo() {
    this.spinnerService.start({
      blockActions: true
    });

    combineLatest(
      this.driversList$,
      this.store.select(addVehicleInfoQuery.selectNewVehicleList),
      this.store.select(userQuery.selectUserStateCode),
      this.isPartner$,
      (driversList, vehicleList, userState, isPartner) => ({
        driversList: driversList,
        vehicleList: vehicleList,
        userState: userState,
        isPartner: isPartner
      })
    )
      .pipe(take(1))
      .subscribe(state => {
        if (
          state.isPartner &&
          this.copyService
            .getCopy('policy.statesForVehicleAssignment')
            .find(stateName => stateName === state.userState)
        ) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/uw-questions']
            })
          );
        } else if (
          _get(state.driversList, 'length', 0) >
          2 * _get(state.vehicleList, 'length', 0)
        ) {
          this.analyticsFacade.trackPage(OverviewAnalytics.TooManyDriversAnalytic);
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/driver-selection']
            })
          );
        }
      });
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private handleError() {
    this.store
      .select(addVehicleBindQuery.selectVehicleANSNotificationError)
      .pipe(takeUntil(this.stop$))
      .subscribe(agentNotificationError => {
        if (agentNotificationError) {
          this.errorEvent.emit();
          this.spinnerService.stop();
        } else {
          this.errorEvent.emit({ hasError: false });
        }
      });
  }
}
